import axios from "axios";
import { MakeCancelable } from "./CancelablePromise";
import qs from "qs";


const config = {
  // "XSRF-TOKEN": xsrfToken,
  // version: "httpOnly",
};

export var instance = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  withCredentials: false,
  transformRequest: [
    (data) => {
      return data;
    },
    ...axios.defaults.transformRequest,
  ],
});

registerInterceptor(instance);

export const addSocketIdInterceptor = (socket) => {
  instance.interceptors.request.use((config) => {
    // Check if the request contains multipart/form-data
    if (
      config.headers["Content-Type"] &&
      config.headers["Content-Type"].startsWith("multipart/form-data")
    ) {
      return config; // Skip adding socketId to the request
    }
    
    // Skip adding socketId for reports endpoints
    if (config.url && config.url.includes("/reports/")) {
      return config; // Skip adding socketId to reports requests
    }

    // Skip adding socketId for endpoints with strict backend body validation
    // (permission, team and client-link routes use zod strictObject, which rejects socketId).
    if (
      config.url &&
      (config.url.includes("/permissions") ||
        config.url.includes("/teams") ||
        config.url.includes("/client-links"))
    ) {
      return config;
    }

    config.data = {
      ...config.data,
      socketId: socket.id,
    };
    return config;
  });
};

function registerInterceptor(instance) {
  instance.interceptors.response.use(
    function (response) {
      // Success
      return Promise.resolve(response.data);
    },
    async function (error) {
      // Handle response error
      if (error.response) {
        // The request was made and the server responded with a status code
        const status = error.response.status;
        const message = error.response.data.message;

        // A missing/absent auth token comes back as 400 "Token not found"
        // (not 401). Without handling it here, the app keeps firing tokenless
        // requests that all 400, and the user is stranded on a blank dashboard
        // with no way back to login. Treat it like an auth failure: clear
        // credentials and bounce to /login. Scoped to the exact auth message
        // so ordinary 400 validation errors are unaffected.
        if (status === 400 && message === "Token not found") {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          return Promise.reject({
            data: error.response.data,
            status,
            statusText: error.response.statusText,
          });
        }

        if (status === 401 ) {
          console.log("error", message  )
          
          // Try to refresh the token
          const refreshToken = localStorage.getItem("refreshToken");
          if (refreshToken) {
            try {
              // In the body, never the URL: query strings are written to the
              // server access logs (project-management#540).
              const refreshResponse = await post("/v1/auth/refresh-token", { refreshToken });
              if (refreshResponse.success) {
                localStorage.setItem("token", refreshResponse.data);
                window.dispatchEvent(new Event("tpe:token-refreshed"));
                // Retry the original request
                const originalRequest = error.config;
                originalRequest.headers["Authorization"] = `Bearer ${refreshResponse.data}`;
                return instance(originalRequest);
              }
            } catch (refreshError) {
              console.log("Refresh token failed, logging out");
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
              // Redirect to the login page if not already redirected
              window.location.href = "/login";
            }
          } else {
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            // Redirect to the login page if not already redirected
            window.location.href = "/login";
          }
        }

        // Return the error details as a rejected promise
        return Promise.reject({
          data: error.response.data,
          status: status,
          statusText: error.response.statusText,
        });
      } else if (error.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        return Promise.reject({
          data: "",
          status: 0,
          statusText: "No Response",
        });
      } else {
        // Something happened in setting up the request that triggered an Error
        return Promise.reject({
          data: "",
          status: 0,
          statusText: error.message,
        });
      }
    }
  );
}

// Add an interceptor to set the authorization header before each request
instance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("token");

    config.headers["ngrok-skip-browser-warning"] = true;

    if (accessToken) {
      config.headers["Authorization"] = accessToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

async function get(url, data) {
  const cancelablePromise = MakeCancelable(
    instance.get(url, { ...data, ...config })
  );

  const response = await cancelablePromise.promise;
  return response;
}

async function post(url, data) {
  const cancelablePromise = MakeCancelable(
    instance.post(url, {
      ...data,
      ...config,
    })
  );

  const response = await cancelablePromise.promise;
  return response;
}

async function put(url, data) {
  const cancelablePromise = MakeCancelable(
    instance.put(url, data, {
      params: config,
      paramsSerializer: (params) =>
        qs.stringify(params, {
          allowDots: true,
          arrayFormat: "indices",
          encode: false,
        }),
    })
  );

  const response = await cancelablePromise.promise;
  return response;
}

// `options` is extra axios config for this one call (e.g. a `signal` to abort it).
async function patch(url, data, params, options = {}) {
  const cancelablePromise = MakeCancelable(
    instance.patch(url, data, {
      ...options,
      params: { ...params, ...config },
      paramsSerializer: (params) =>
        qs.stringify(params, {
          allowDots: true,
          arrayFormat: "indices",
          encode: false,
        }),
    })
  );

  const response = await cancelablePromise.promise;
  return response;
}

// PATCH that survives the page being closed or reloaded (fetch keepalive;
// XHR, which axios uses, is cancelled on unload). Mirrors the axios
// instance's base URL and Authorization header. Resolves with the response
// body and rejects in the same shape as the axios interceptor.
async function patchKeepalive(url, data) {
  const token = localStorage.getItem("token");
  const response = await fetch(`${instance.defaults.baseURL || ""}${url}`, {
    method: "PATCH",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: token }),
    },
    body: JSON.stringify(data),
  });
  const body = await response.json().catch(() => "");
  if (!response.ok) {
    return Promise.reject({
      data: body,
      status: response.status,
      statusText: response.statusText,
    });
  }
  return body;
}

async function destroy(url, data = {}) {
  const cancelablePromise = MakeCancelable(
    instance.delete(url, {
      params: { ...data, ...config },
    })
  );

  const response = await cancelablePromise.promise;
  return response;
}

async function upload(url, data) {
  Object.keys(config).forEach((key) => {
    data.append(key, config[key]);
  });
  const cancelablePromise = MakeCancelable(instance.post(url, data));

  const response = await cancelablePromise.promise;
  return response;
}

const download = async (url, data) => {
  const cancelablePromise = MakeCancelable(
    instance.get(url, {
      responseType: "blob",
      contentType: "application/octet-stream",
      ...data,
    })
  );

  const response = await cancelablePromise.promise;
  return response;
};

const submitFormData = async (url, formData) => {
  const cancelablePromise = MakeCancelable(
    instance.post(url, formData, {
      headers: {
        ...config,
        "Content-Type": "multipart/form-data",
      },
    })
  );

  const response = await cancelablePromise.promise;
  return response;
};

function isLoggedIn() {
  return false;
}
export {
  post,
  get,
  put,
  patch,
  patchKeepalive,
  destroy,
  download,
  upload,
  isLoggedIn,
  submitFormData,
};
